/**
 * The agent as a package, read live from its platform: the instructions it runs
 * with, the named inputs each dataset item fills, its model, tools, the shape of
 * its answer, and the settings an experiment can fix. Read-only — the agent is
 * changed on its platform, and each experiment keeps its own snapshot.
 */

import { Box, BoxHeader, CodeBlock } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { EmptyState, Skeleton, errorText } from "@/components/ui/states";
import { formatJson, formatRelative } from "@/lib/format";
import { InputsList, ModelCard, RunSettingsList, Section, ToolsList } from "./agent-package-parts";
import { useAgentSpec } from "./hooks";
import { InstructionsBlock } from "./instructions-block";
import { OutputSchema } from "./output-schema";
import type { Agent, AgentSpec } from "./types";

/** The service wraps platform errors in this; the page says it once, in its own words. */
const PLATFORM_PREFIX = /^The agent platform did not answer:\s*/;

function Package({ spec }: { spec: AgentSpec }) {
  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
      <div className="flex min-w-0 flex-col gap-6">
        <Section title="Instructions">
          {spec.instructions ? (
            <InstructionsBlock text={spec.instructions} />
          ) : (
            <p className="px-4 py-3 text-[13px] text-muted">
              The platform returned no instructions for this agent.
            </p>
          )}
        </Section>
        <Section title="Output">
          {spec.output_schema ? (
            <OutputSchema schema={spec.output_schema} />
          ) : (
            <p className="px-4 py-3 text-[13px] text-muted">
              Free text. The agent declares no output schema.
            </p>
          )}
        </Section>
        {Object.keys(spec.raw).length > 0 && (
          <details className="box">
            <summary className="cursor-pointer px-4 py-3 text-sm font-semibold">Raw config</summary>
            <CodeBlock label="Agent config">{formatJson(spec.raw)}</CodeBlock>
          </details>
        )}
      </div>
      <aside className="flex flex-col gap-6">
        <Section title="Inputs" count={spec.inputs.length}>
          <InputsList inputs={spec.inputs} />
        </Section>
        <Section title="Model">
          <ModelCard spec={spec} />
        </Section>
        <Section title="Tools" count={spec.tools.length}>
          <ToolsList tools={spec.tools} />
        </Section>
        <Section title="Run settings" count={spec.run_settings.length}>
          <RunSettingsList settings={spec.run_settings} />
        </Section>
      </aside>
    </div>
  );
}

const isEmpty = (s: AgentSpec) =>
  !s.instructions && s.inputs.length === 0 && !s.model && s.tools.length === 0 && !s.output_schema;

export function AgentPackage({ agent }: { agent: Agent }) {
  const spec = useAgentSpec(agent.id);
  const fetchedAt = spec.dataUpdatedAt ? new Date(spec.dataUpdatedAt).toISOString() : null;

  return (
    <div className="flex flex-col gap-4">
      <Box>
        <BoxHeader title="Agent package">
          <span className="src">
            {spec.data?.version && `v${spec.data.version} · `}read from {agent.connector_label}
            {fetchedAt && ` · ${formatRelative(fetchedAt)}`}
          </span>
          <span className="flex-1" />
          <Button size="sm" onClick={() => void spec.refetch()} disabled={spec.isFetching}>
            Refresh
          </Button>
        </BoxHeader>
        {spec.isPending && <Skeleton label="Loading the agent package" />}
        {spec.isError && (
          <div role="alert" className="m-4 rounded-lg bg-err-bg px-4 py-3 text-[13px] text-err">
            Couldn't read the agent from {agent.connector_label}:{" "}
            {errorText(spec.error, "the platform did not answer. Try again shortly.").replace(
              PLATFORM_PREFIX,
              "",
            )}
          </div>
        )}
        {spec.data && isEmpty(spec.data) && (
          <EmptyState title="Nothing to show yet">
            {agent.connector_label} returned no instructions, inputs, model or tools for this agent.
          </EmptyState>
        )}
        {spec.data && !isEmpty(spec.data) && (
          <p className="px-4 py-3 text-[13px] text-muted">
            Each dataset item fills the inputs; an experiment fixes the run settings. Change the agent in{" "}
            {agent.connector_label}, then run a new experiment.
          </p>
        )}
      </Box>
      {spec.data && !isEmpty(spec.data) && <Package spec={spec.data} />}
    </div>
  );
}
